import {Icon} from '@udemy-v2/icons';
import {MarketplaceOnly} from '@udemy-v2/react-brand-components';
import {TextInputProps} from '@udemy-v2/react-form-components';
import {ButtonStyleType, Button, ButtonProps, IconButton} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {FormGroup, TextInputContainer, TextInput} from '@udemy-v2/react-form-components';
import {safelySetInnerHTML} from '@udemy-v2/utils';
import classNames from 'classnames';
import React from 'react';

// import type {DiscountAttempt} from '@udemy-v2/api-shopping';

import {CartOptIn} from './cart-opt-in.react-component';
import {CouponForm} from './coupon-form.react-component';
import {MODES, ModesKeys, SOURCE_TYPE, DISCOUNT_STATUSES} from './constants';
import {DiscountAttempt, CouponStore} from '@udemy-v2/api-shopping';

import {isDiscountRemovable} from './utils';

import styles from './redeem-coupon.module.scss';

interface ValidDiscountProps {
    removeCoupon: () => void;
    attemptDiscount: DiscountAttempt;
    isInCheckoutPane?: boolean;
}

export function ValidDiscount({
    removeCoupon,
    attemptDiscount,
    isInCheckoutPane = false,
}: ValidDiscountProps) {
    const {interpolate, gettext} = useI18n();

    if (attemptDiscount.status !== DISCOUNT_STATUSES.APPLIED) return null;

    const translatedText = interpolate(
        gettext('<b>%(couponCode)s</b> is applied'),
        {couponCode: attemptDiscount.code || ''},
        true,
    );

    const SOURCE_TYPE_TEXT = {
        UDEMY_COUPON: gettext('Udemy coupon'),
        INSTRUCTOR_COUPON: gettext('Instructor coupon'),
    };

    let sourceTypeText: string | null = null;
    switch (attemptDiscount.source_type) {
        case SOURCE_TYPE.UDEMY_COUPON:
            sourceTypeText = SOURCE_TYPE_TEXT.UDEMY_COUPON;
            break;
        case SOURCE_TYPE.INSTRUCTOR_COUPON:
            sourceTypeText = SOURCE_TYPE_TEXT.INSTRUCTOR_COUPON;
            break;
    }

    return (
        <div
            data-purpose="code"
            className={classNames(styles.code, {
                [styles['code-in-checkout-pane']]: isInCheckoutPane,
            })}
        >
            <div className={styles['code-text']}>
                <ul>
                    <li
                        {...safelySetInnerHTML({
                            descriptionOfCaller: 'redeem-coupon:code-text',
                            html: translatedText,
                            dataPurpose: 'code-text',
                        })}
                    />
                    {sourceTypeText && <li>{sourceTypeText}</li>}
                </ul>
            </div>
            {isDiscountRemovable(attemptDiscount) && (
                <IconButton
                    className={styles['code-icon-btn']}
                    onClick={removeCoupon}
                    udStyle="ghost"
                    data-purpose="remove-coupon"
                >
                    <Icon
                        glyph="close"
                        label={gettext('Remove coupon')}
                        color={isInCheckoutPane ? 'neutral' : 'info'}
                        size="small"
                    />
                </IconButton>
            )}
        </div>
    );
}

interface EnableCouponInputModeButtonProps {
    onEnterInputMode: () => void;
    enableCouponInputModeButtonProps?: ButtonProps;
}

export function EnableCouponInputModeButton({
    onEnterInputMode,
    enableCouponInputModeButtonProps = {},
}: EnableCouponInputModeButtonProps) {
    const {gettext} = useI18n();

    return (
        <Button
            udStyle="ghost"
            data-purpose="show-coupon-form"
            onClick={onEnterInputMode}
            {...enableCouponInputModeButtonProps}
        >
            {gettext('Apply Coupon')}
        </Button>
    );
}

interface RemoveCouponButtonProps {
    onRemoveCoupon: () => void;
    removeCouponButtonProps?: ButtonProps;
}

export function RemoveCouponButton({
    onRemoveCoupon,
    removeCouponButtonProps = {},
}: RemoveCouponButtonProps) {
    const {gettext} = useI18n();

    return (
        <Button
            udStyle="ghost"
            data-purpose="remove-coupons"
            onClick={onRemoveCoupon}
            {...removeCouponButtonProps}
        >
            {gettext('Remove Coupon')}
        </Button>
    );
}

export interface RedeemCouponProps {
    currentMode: ModesKeys;
    currentDiscountAttempts: DiscountAttempt[];
    error?: string;
    submittedCode: string;
    inputCode: string;
    inputOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    inputOnSubmit: () => void;
    inputIsDisabled: boolean;
    onEnterInputMode: () => void;
    removeCouponCode: (code: string) => () => void;
    className?: string;
    inputModeClassName?: string;
    enableCouponInputModeButtonProps?: ButtonProps;
    showCouponInputModeButton?: boolean;
    showTitle?: boolean;
    showCartOptIn?: boolean;
    isInCheckoutPane?: boolean;
    couponStore?: CouponStore;
    showDiscountsAsTextInput?: boolean;
    size?: TextInputProps['size'];
    submitButtonStyle?: ButtonStyleType;
}

export function RedeemCoupon({
    currentMode,
    currentDiscountAttempts,
    error,
    submittedCode,
    inputCode,
    inputOnChange,
    inputOnSubmit,
    inputIsDisabled,
    onEnterInputMode,
    removeCouponCode,
    className,
    inputModeClassName,
    enableCouponInputModeButtonProps = {},
    showCouponInputModeButton = true,
    showTitle = false,
    showCartOptIn = false,
    isInCheckoutPane = false,
    showDiscountsAsTextInput = false,
    couponStore,
    size,
    submitButtonStyle,
}: RedeemCouponProps) {
    const {gettext, locale} = useI18n();
    const appliedDiscounts = currentDiscountAttempts.filter(
        (discount) => discount.status === DISCOUNT_STATUSES.APPLIED,
    );
    const isInputMode = [MODES.INPUT, MODES.PENDING].includes(currentMode);
    const shouldHideCouponForm = !isInputMode;

    if (currentMode === MODES.OFF) {
        return null;
    }

    return (
        <div
            className={classNames(styles.container, className, {
                [classNames(styles['input-mode'], inputModeClassName)]: isInputMode,
            })}
            data-purpose="redeem-coupon-container"
            data-testid="redeem-coupon"
        >
            {showTitle && (
                <h3 className={styles.title} data-purpose="coupon-title">
                    {gettext('Coupons')}
                </h3>
            )}

            <MarketplaceOnly>
                {showCartOptIn && couponStore && <CartOptIn couponStore={couponStore} />}
            </MarketplaceOnly>

            <>
                {appliedDiscounts.length > 0 && !showDiscountsAsTextInput && (
                    <div className={styles.codes}>
                        {appliedDiscounts.map((discount, index) => (
                            <ValidDiscount
                                key={`discount-${discount.code}-${index}`}
                                attemptDiscount={discount}
                                removeCoupon={removeCouponCode(discount.code || '')}
                                isInCheckoutPane={isInCheckoutPane}
                            />
                        ))}
                    </div>
                )}

                {showCouponInputModeButton && !isInCheckoutPane && (
                    <EnableCouponInputModeButton
                        onEnterInputMode={onEnterInputMode}
                        enableCouponInputModeButtonProps={{
                            ...enableCouponInputModeButtonProps,
                        }}
                    />
                )}

                {!shouldHideCouponForm && (
                    <CouponForm
                        onChange={inputOnChange}
                        onSubmit={inputOnSubmit}
                        disabled={inputIsDisabled}
                        error={error}
                        submittedCode={submittedCode}
                        inputCode={inputCode}
                        currentMode={currentMode}
                        isInCheckoutPane={isInCheckoutPane}
                        size={size}
                        submitButtonStyle={submitButtonStyle}
                    />
                )}
                {showDiscountsAsTextInput && appliedDiscounts.length > 0 && (
                    <div className={styles['applied-discounts']}>
                        {appliedDiscounts.map((discount, idx) => {
                            let noteText = undefined;
                            if (discount.source_type === SOURCE_TYPE.INSTRUCTOR_COUPON) {
                                noteText = gettext('Instructor coupon');
                            }
                            // For the text input display mode, don't show the source type unless there's a legal requirement (in this case, only in Japan)
                            if (
                                discount.source_type === SOURCE_TYPE.UDEMY_COUPON &&
                                locale === 'ja_JP'
                            ) {
                                noteText = gettext('Udemy coupon');
                            }
                            return (
                                <FormGroup
                                    label={gettext('Applied coupons')}
                                    labelProps={{className: 'ud-sr-only'}}
                                    note={noteText}
                                    key={idx}
                                >
                                    <TextInputContainer>
                                        <TextInput
                                            value={discount.code}
                                            disabled={true}
                                            size={'small'}
                                        />
                                        <div className={styles['text-input-applied-coupon']}>
                                            <span
                                                className={classNames(
                                                    'ud-text-sm',
                                                    styles['applied-text'],
                                                )}
                                            >
                                                {gettext('Applied!')}
                                            </span>
                                            {isDiscountRemovable(discount) && (
                                                <IconButton
                                                    onClick={removeCouponCode(discount.code)}
                                                    udStyle="ghost"
                                                    size={'small'}
                                                    className={styles['clear-button']}
                                                >
                                                    <Icon
                                                        glyph="close"
                                                        color="neutral"
                                                        label={gettext('Remove coupon')}
                                                    />
                                                </IconButton>
                                            )}
                                        </div>
                                    </TextInputContainer>
                                </FormGroup>
                            );
                        })}
                    </div>
                )}
            </>
        </div>
    );
}
